import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Whether an element has been scrolled near the viewport, once.
 *
 * Once true it stays true: the folder uses this to decide whether to fetch a
 * thumbnail, and a tile that scrolled out and back must not re-enter a loading
 * state for something already in hand.
 *
 * Local to the client card rather than shared — it is small, and a shared version
 * would want options this caller has no use for.
 */
export const useInView = <T extends Element>(enabled = true): [RefObject<T | null>, boolean] => {
  const ref = useRef<T | null>(null);

  // Where there is no IntersectionObserver — jsdom, and browsers old enough to
  // predate it — everything counts as visible. Degrading to "fetch eagerly" keeps
  // the image working; degrading to "never visible" would leave a permanent
  // placeholder that no scroll could ever resolve.
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (!enabled || inView) return;

    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      // A screen's worth of margin, so a tile is fetched just before it is scrolled to
      // rather than blank at the moment it arrives.
      { rootMargin: '300px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, inView]);

  return [ref, inView];
};

export default useInView;
